import { asBool, asRecord, asString, assertArray } from "@/data/mapper/common/mapper-utils";
import { PatientInfoMapper } from "@/data/mapper/patient/patient-info-mapper";
import { normalizeRole } from "@/domain/entity/auth/user-session-entity";
import { MedicalExpert } from "@/domain/entity/doctor/medical-expert-entity";
import { PatientSummary } from "@/domain/entity/patient/patient-info-entity";

export class MedicalExpertMapper {
  static toEntity(dto: unknown): MedicalExpert {
    const map = asRecord(dto);
    const hospital = getDoctorHospital(map);
    const patients = getDoctorPatients(map);
    return {
      role: normalizeRole(asString(map.role, "Doctor")),
      uuid: asString(map.uuid ?? map.doctorUuid),
      name: asString(map.name, "-"),
      email: asString(map.email, "-"),
      phone: asString(map.phone, "-"),
      countryCode: asString(map.countryCode, "kr"),
      activeState: getDoctorActiveState(map),
      isAccountSetup:
        map.isAccountSetup == null && map.accountSetup == null
          ? null
          : asBool(map.isAccountSetup ?? map.accountSetup),
      loginAt: map.loginAt == null ? null : asString(map.loginAt),
      hospitalCode: hospital.code,
      hospitalName: hospital.name,
      memo: map.memo == null ? null : asString(map.memo),
      ...(patients == null ? {} : { patients }),
    };
  }

  static toEntityList(dto: unknown): MedicalExpert[] {
    return assertArray(dto, "MedicalExpertMapper::toEntityList").map((item) =>
      MedicalExpertMapper.toEntity(item),
    );
  }
}

function getDoctorActiveState(map: Record<string, unknown>): string {
  if (map.isDeactivated != null) {
    return asBool(map.isDeactivated) ? "deactivate" : "active";
  }

  return asString(map.activeState ?? "active");
}

function getDoctorHospital(map: Record<string, unknown>): { code: string | null; name: string | null } {
  const hospital = asRecord(map.hospital);
  const hospitalName = asRecord(map.hospitalName);
  return {
    code: firstString(
      map.hospitalCode,
      hospital.hospitalCode,
      hospital.code,
      hospitalName.hospitalCode,
      hospitalName.code,
    ),
    name: firstString(
      map.hospitalName,
      hospital.hospitalName,
      hospital.name,
      hospitalName.hospitalName,
      hospitalName.name,
    ),
  };
}

function getDoctorPatients(map: Record<string, unknown>): PatientSummary[] | undefined {
  const rawPatients =
    map.patients ??
    map.patientList ??
    map.patientInfoList ??
    map.patientUserList ??
    map.chargePatientList ??
    map.chargedPatients;

  if (rawPatients === undefined) return undefined;
  return Array.isArray(rawPatients)
    ? rawPatients.map((item) => PatientInfoMapper.toEntity(item))
    : [];
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (value == null || typeof value === "object") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}
